import { cssMerge } from '@volvo-cars/css/utils';
import { useLocalizedString } from '@volvo-cars/react-locale-provider';
import { useComposedRefs } from '@volvo-cars/react-utils';
import type React from 'react';
import {
  Children,
  forwardRef,
  isValidElement,
  useEffect,
  useRef,
  useState,
} from 'react';
import { label as selectLabelTranslations } from '../i18n/select/i18n';
import { ErrorMessage } from './error-message';
import { Hint } from './hint';
import type { BaseInputProps, GlobalHTMLAttributes } from './types';
import { useIds } from './use-ids';

function LocalizedSelectLabel() {
  const label = useLocalizedString(selectLabelTranslations);

  return label;
}

function mountSelectedContent(select: HTMLSelectElement) {
  if (
    !select.closest('.custom-select') ||
    select.querySelector(':scope > button') ||
    !CSS.supports('appearance', 'base-select') ||
    !('HTMLSelectedContentElement' in window)
  ) {
    return;
  }

  // The implicit native button cannot ellipsize its text. Give the browser
  // a selectedcontent slot to manage, separate from React's option children.
  // Create it after hydration: older HTML parsers and React 18 do not
  // support this button in server-rendered select markup.
  const button = document.createElement('button');
  button.type = 'button';
  const selectedContent = document.createElement('selectedcontent');
  button.append(selectedContent);
  select.prepend(button);

  // Preserve the implicit button's plain-text label, including option[label].
  // Also refresh edited option text in browsers that only update the native
  // slot on selection. Observing the slot covers programmatic value changes
  // without duplicating the select's value in React state.
  const syncLabel = () => {
    const label = select.selectedOptions[0]?.label ?? '';
    if (
      selectedContent.textContent !== label ||
      selectedContent.firstElementChild
    ) {
      selectedContent.textContent = label;
    }
  };
  syncLabel();
  const observer = new MutationObserver(syncLabel);
  observer.observe(select, {
    childList: true,
    subtree: true,
    characterData: true,
    attributes: true,
    attributeFilter: ['label'],
  });
  // A form owner can appear or be replaced after this select mounts.
  // Capture reset at the tree root and check the current owner each time.
  const root = select.getRootNode();
  let resetTimer: ReturnType<typeof setTimeout> | undefined;
  const onReset = (event: Event) => {
    if (event.target !== select.form) return;
    // Native reset does not always refresh selectedcontent. A microtask runs
    // too early for a reset-button click; read the final value in the next task.
    clearTimeout(resetTimer);
    resetTimer = setTimeout(syncLabel, 0);
  };
  root.addEventListener('reset', onReset, true);
  return () => {
    root.removeEventListener('reset', onReset, true);
    clearTimeout(resetTimer);
    observer.disconnect();
    button.remove();
  };
}

export interface SelectProps
  extends GlobalHTMLAttributes, BaseInputProps<HTMLSelectElement> {
  /**
   * @default 'top-label'
   */
  variant?: 'top-label' | 'floating-label';

  children: React.ReactNode;

  /**
   * Makes the select required.
   */
  required?: boolean;

  enterKeyHint?: 'done' | 'go' | 'next' | 'previous' | 'search' | 'send';

  /**
   * The name of the input to use when submitting the form.
   */
  name: string;

  /**
   * A concise label for the input.
   */
  label: string;

  /**
   * What type of information to autocomplete in the select.
   *
   * @see https://developer.mozilla.org/en-US/docs/Web/HTML/Attributes/autocomplete
   */
  autoComplete?:
    | 'off'
    | 'on'
    | 'organization-title'
    | 'organization'
    | 'address-level4'
    | 'address-level3'
    | 'address-level2'
    | 'address-level1'
    | 'country'
    | 'country-name'
    | 'cc-exp-month'
    | 'cc-exp-year'
    | 'cc-type'
    | 'transaction-currency'
    | 'language'
    | 'sex';

  /**
   * Set the error message of a select and mark it invalid.
   */
  errorMessage?: string;

  /**
   * Force the input to be invalid.
   *
   * @default false
   */
  'aria-invalid'?: boolean;

  /**
   * Default value of an uncontrolled input.
   */
  defaultValue?: string | string[];

  /**
   * Value of the input.
   *
   * Makes the input controlled.
   */
  value?: string | string[];

  /**
   * Fires when the select’s value is changed.
   */
  onChange?: React.ChangeEventHandler<HTMLSelectElement>;
}

/**
 * Select lets the users select one option from a predefined list of 3 or more options. When you have fewer
 * options, consider using a RadioGroup.
 *
 * Review the [React docs](https://react.dev/reference/react-dom/components/select) for more details
 * on using selects in React.
 */
export const Select = forwardRef<HTMLSelectElement, SelectProps>(
  function Select(
    {
      variant = 'top-label',
      hint,
      id,
      label,
      hidden,
      dir,
      errorMessage: errorMessageProp,
      lang,
      translate,
      slot,
      value,
      defaultValue,
      children,
      className,
      ...props
    }: SelectProps,
    ref: React.ForwardedRef<HTMLSelectElement>,
  ) {
    const selectRef = useRef<HTMLSelectElement>(null);
    const composedRef = useComposedRefs(ref, selectRef);
    useEffect(() => {
      if (selectRef.current) return mountSelectedContent(selectRef.current);
    }, [className]);

    let hasEmptyChild = false;
    Children.forEach(children, (child) => {
      if (
        isValidElement(child) &&
        child.type === 'option' &&
        typeof child.props === 'object' &&
        child.props !== null &&
        'value' in child.props &&
        typeof child.props.value === 'string' &&
        child.props.value === ''
      ) {
        hasEmptyChild = true;
      }
    });
    const [isUncontrolledBlank, setIsUncontrolledBlank] =
      useState(!defaultValue);
    const controlled = typeof value === 'string';
    const { inputId, errorId, hintId } = useIds(id);
    // Hide error message and error state when input is disabled
    const errorMessage = !props.disabled ? errorMessageProp : undefined;
    const ariaInvalid = props['aria-invalid'] || !!errorMessage;
    const isBlank = controlled ? !value : isUncontrolledBlank;
    return (
      <div
        id={id}
        className={cssMerge(
          variant === 'floating-label'
            ? 'input-floating-label'
            : 'flex-col items-start gap-8',
          className,
        )}
        hidden={hidden}
        dir={dir}
        lang={lang}
        translate={translate}
        slot={slot}
      >
        <label htmlFor={inputId}>{label}</label>
        <select
          {...props}
          ref={composedRef}
          id={inputId}
          onPointerDown={(event) => {
            props.onPointerDown?.(event);

            const select = event.currentTarget;
            if (
              !event.defaultPrevented &&
              event.isPrimary &&
              event.pointerType === 'touch' &&
              // The native button and selectedcontent are inert: trigger
              // presses target the select. Leave option interactions native.
              event.target === select &&
              getComputedStyle(select).appearance === 'base-select' &&
              select.matches(':open')
            ) {
              // Touch light-dismiss closes the picker before the compatibility
              // mousedown, which would open it again. Suppress that mouse event
              // and keep focus on the field instead of a now-hidden option.
              event.preventDefault();
              select.focus({ preventScroll: true });
            }
          }}
          onChange={
            controlled
              ? props.onChange
              : (event) => {
                  props.onChange?.(event);
                  setIsUncontrolledBlank(!event.target.value);
                }
          }
          value={value}
          defaultValue={defaultValue}
          data-blank={variant === 'floating-label' && isBlank ? '' : undefined}
          aria-invalid={ariaInvalid ? 'true' : undefined}
          aria-errormessage={errorMessage ? errorId : undefined}
          aria-describedby={[errorMessage && errorId, hint && hintId]
            .filter(Boolean)
            .join(' ')}
        >
          {/*
          By default in browsers, the first <option> of a blank <select> will be selected.
          We create an option with the localized word "Select" and an empty value instead of
          automatically selecting the first option.
          This option is hidden as soon as another option is selected or if any other option is
          selected by deafult.
          Due to issues in Safari on iPads we don't dynamically remove it from the DOM when the
          select is changed but instead use `hidden` and `disabled` to hide it.
        */}
          {!defaultValue && !hasEmptyChild && (
            <option value="" hidden={!isBlank} disabled={!isBlank}>
              {variant === 'top-label' ? <LocalizedSelectLabel /> : label}
            </option>
          )}
          {children}
        </select>
        <ErrorMessage
          errorMessage={errorMessage}
          id={errorId}
          className={variant === 'floating-label' ? 'mt-8' : undefined}
        />
        {hint && (
          <Hint
            id={hintId}
            className={variant === 'floating-label' ? 'mt-8' : undefined}
          >
            {hint}
          </Hint>
        )}
      </div>
    );
  },
);
